import {Button, Checkbox, Col, Collapse, Divider, Input, Radio, Row, Space} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {isEqual} from 'underscore';
import {formatTitleCase} from "@lib/format";
import {defaultIssueFilterProps, fixFilterObjectEmptyArrays} from "@components/issues-filter-drawer/IssueFilterDrawer";
import {IssueFilterDrawer} from "@components/issues-filter-drawer";
import {FilterIcon} from "@components/icons/Icons";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {SCREEN, useWindowSize} from "@components/window-size-provider";
import {useGetFilterOptionsQuery} from "@mwServices/Issue";
import {transformEra} from "@lib/utils";

const { Panel } = Collapse;

const IssueFiltersContent = ({
                                  filters,
                                  setFilters,
                                  defaultFilters,
                              }) => {
    const subscriptionType = useSubscriptionType();
    const showPriceInfo = useMemo(() => ( subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);

    const queryParams = useMemo(() => ({filters: {...defaultFilters}}), [defaultFilters]);
    const filterOptionsQuery = useGetFilterOptionsQuery(queryParams);

    const {publishers, eras, is_hot, is_key, is_rare, owned} = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data as any;
        return {
            publishers: [],
            eras: [],
            is_hot: 0,
            is_key: 0,
            is_rare: 0,
            owned: 0,
        };
    }, [filterOptionsQuery]);

    return (
        <>
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold">Sort by:</div>
                    <br/>

                    <Radio.Group
                        className="pad-left-10"
                        onChange={(event) => setFilters({...filters, sort: event.target.value})}
                        value={filters.sort}
                    >
                        <Space direction="vertical">
                            <Radio value={0} >Title A-Z</Radio>
                            <Radio value={10} >Title Z-A</Radio>
                            <Radio value={showPriceInfo ? 29 : null} disabled={!showPriceInfo}>Raw Value (Highest to Lowest)</Radio>
                            <Radio value={showPriceInfo ? 31 : null} disabled={!showPriceInfo}>Slab Value (Highest to Lowest)</Radio>
                            <Radio value={showPriceInfo ? 2 : null} disabled={!showPriceInfo}>Current Market Trend (%) (Highest to Lowest)</Radio>
                        </Space>
                    </Radio.Group>

                    <br/>
                    {!showPriceInfo && (<p><br/><a className="upgrade-link" href="/plans/unlimited/">*Upgrade to use all filter options.</a></p>)}
                </Col>
            </Row>
            <Divider />
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold">Filter by:</div>

                    <Collapse className="filtersAccordion" ghost defaultActiveKey={[1,2,3,4,5]} expandIconPosition={"end"}>

                        <Panel header="Regular / Variants" key={1}>
                            <Space direction="vertical">
                                <Checkbox checked={true} disabled>{formatTitleCase('Regular')}</Checkbox>
                                <Checkbox checked={!!filters.showVariants} onChange={(event) => setFilters({...filters, showVariants: event.target.checked})}>{formatTitleCase('Show Variants')}</Checkbox>
                            </Space>
                        </Panel>

                        {subscriptionType.isLoggedIn && (
                            <Panel header="In Collection" key={2}>
                                <Space direction="vertical">
                                    <Checkbox
                                        checked={!!filters?.inCollection}
                                        onChange={(event) => setFilters({
                                            ...filters, inCollection:
                                            event.target.checked
                                        })}
                                    >{formatTitleCase('Items In Your Collection')} ({owned})
                                    </Checkbox>
                                </Space>
                            </Panel>
                        )}


                        <Panel header="Barcode" key={3}>
                            <Space direction="vertical">
                                <Input
                                    name="barcode"
                                    type="text"
                                    placeholder="barcode..."
                                    value={filters.barcode}
                                    onChange={(event) => { setFilters({...filters, barcode: event.target.value}) }}
                                />
                            </Space>
                        </Panel>

                        <Panel header="Hot / Key / Rare" key={4}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={filters.isHot}
                                    onChange={() => setFilters({...filters, isHot: !filters.isHot})}
                                >Hot ({is_hot})</Checkbox>
                                <Checkbox
                                    checked={filters.isKey}
                                    onChange={() => setFilters({...filters, isKey: !filters.isKey})}
                                >Key ({is_key})</Checkbox>
                                <Checkbox
                                    checked={filters.isRare}
                                    onChange={() => setFilters({...filters, isRare: !filters.isRare})}
                                >Rare ({is_rare})</Checkbox>
                            </Space>
                        </Panel>

                        <Panel header="Era" key={5}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.multipleDateRanges}
                                    onChange={(event) => {
                                        setFilters({...filters, multipleDateRanges: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {eras.map((era, i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.multipleDateRanges || filters.multipleDateRanges.findIndex(dr => !!dr.id && dr.id == era.id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.multipleDateRanges && !event.target.checked){
                                                setFilters({...filters, multipleDateRanges: eras.map(transformEra).filter(item => item.id !== era.id)});
                                            } else if(filters.multipleDateRanges) {
                                                let filterDateRanges: any = filters.multipleDateRanges ? [...filters.multipleDateRanges] : [];
                                                if (event.target.checked) {
                                                    filterDateRanges = [...filterDateRanges, transformEra(era)];
                                                } else {
                                                    filterDateRanges = !!filters.multipleDateRanges ? filters.multipleDateRanges.filter(item => !!item.id && item.id !== era.id) : [];
                                                }
                                                if (filterDateRanges.length === eras.length) filterDateRanges = undefined;
                                                setFilters({...filters, multipleDateRanges: filterDateRanges});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(era.name, 'Unknown Era')} ({era.count})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>

                    </Collapse>

                </Col>
            </Row>
        </>
    );
};

const IssuesFiltersDrawer = ({
                                 filters,
                                 setFilters,
                                 defaultFilters,
}) => {
    const windowDimensions = useWindowSize();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [newFilters, setNewFilters] = useState(filters);

    useEffect(() => {
        if(!isEqual(newFilters, filters)) {
            setNewFilters(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const onClose = () => {
        setNewFilters(filters);
        setShowFilters(false);
    };

    return (
        <>
            <Button className="greyButton" size={"large"} onClick={() => setShowFilters(true)} >
                {windowDimensions.up(SCREEN.SM) && (<span className="filterButtonText">Filter / Sort</span>)}
                <FilterIcon className="font-12" />
            </Button>

            <IssueFilterDrawer visible={showFilters} onClose={onClose} onSearch={() => {
                setShowFilters(false);

                const fixedFilters = fixFilterObjectEmptyArrays(
                    newFilters,
                    filters,
                    ['publishers', 'multipleDateRanges']);

                if(fixedFilters.showValuesShown === -1) fixedFilters.showValuesShown = undefined;

                if(!isEqual(fixedFilters, filters)) {
                    setFilters(fixedFilters);
                }else if(!isEqual(fixedFilters, newFilters)) {
                    setNewFilters(fixedFilters);
                }
            }}>
                <IssueFiltersContent
                    filters={newFilters}
                    setFilters={setNewFilters}
                    defaultFilters={defaultFilters}
                />
            </IssueFilterDrawer>
        </>
    )
};

IssuesFiltersDrawer.defaultProps = defaultIssueFilterProps;

export default IssuesFiltersDrawer;